import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Building2, Copy, ExternalLink, Link2, Plus, User, UserMinus, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type DepartmentRef, type Paginated, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, projectStatusLabel, useI18n } from '../lib/i18n';
import { addDays, formatDate, formatDateTime, formatNumber } from '../lib/format';
import { anyFilter, useKeptFilters } from '../lib/kept-filters';
import { NO_SUPPORT, PROJECT_STATUSES, type ProjectStatusView } from '../lib/project-status';
import { refusalText } from '../lib/reach';
import { isOpenUrgent } from '../lib/task-order';
import { ProjectStatusMenu } from '../components/ProjectStatusMenu';
import { TaskHolderName } from '../components/TaskHolder';
import { DriveRelated } from './Drive';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
  Tr,
  priorityTone,
  statusTone,
  useToast,
} from '../components/ui';

/**
 * A project's status badge, and until when it is supported — or that it is in
 * support by hand, with no contract.
 */
function ProjectStatusBadge({ project }: { project: Omit<ProjectStatusView, 'id'> }) {
  const { t, locale } = useI18n();
  return (
    <>
      <Badge tone={statusTone(project.status)}>{projectStatusLabel(project.status, locale)}</Badge>
      {project.status === 'SUPPORT' && (project.support?.until || project.supportByHand) && (
        <span className="text-caption text-muted">
          {project.support?.until
            ? t('projects.supportUntil', { date: formatDate(project.support.until, locale) })
            : t('projects.byHand')}
        </span>
      )}
    </>
  );
}

/**
 * Where a project stands with its contracts (2026-10-09): in support until a
 * date, or by hand; completed with a contract starting later, which puts it in
 * support from that day (2026-10-27); completed, cancelled or archived and
 * taking no new work — with a warning when a contract still runs; still being
 * built with a contract running (set back to work after it started), or with
 * one starting later, which then puts it in support and ends it the day before
 * (2026-10-27). Nothing for a project in progress with no contract.
 */
function SupportNote({ project }: { project: Omit<ProjectStatusView, 'id'> }) {
  const { t, locale } = useI18n();
  const support = project.support ?? NO_SUPPORT;
  const running = support.contracts.length > 0;
  const contracts = support.contracts.map((c) => c.number).join(', ');
  const date = formatDate(support.until, locale);

  let tone: 'info' | 'warning' | null = null;
  let text = '';
  const next = support.upcoming?.[0];
  if (project.status === 'SUPPORT') {
    tone = 'info';
    text = running ? t('projects.supportNote', { date, contracts }) : t('projects.supportByHandNote');
  } else if (project.status === 'COMPLETED' && next) {
    // Covered from a later start date: no new work until then, and it ends the day before.
    tone = 'info';
    text = t('projects.supportStartsNote', {
      contracts: next.number,
      date: formatDate(next.startDate, locale),
      end: formatDate(addDays(next.startDate, -1), locale),
    });
  } else if ((project.status === 'CANCELLED' || project.status === 'ARCHIVED') && running) {
    // Closing the project does not stop its contracts: Finance ends them.
    tone = 'warning';
    text = t('projects.closedContractNote', { contracts, date });
  } else if (project.status === 'COMPLETED' || project.status === 'CANCELLED' || project.status === 'ARCHIVED') {
    tone = 'warning';
    text = t(
      project.status === 'ARCHIVED'
        ? 'projects.archivedNote'
        : project.status === 'CANCELLED'
          ? 'projects.cancelledNote'
          : 'projects.completedNote',
    );
  } else if (running) {
    tone = 'info';
    text = t('projects.contractWaitingNote', { contracts, date });
  } else if (next) {
    // Still being built: the contract's start puts it in support, and ends it the day before.
    tone = 'info';
    text = t('projects.contractStartsNote', {
      contracts: next.number,
      date: formatDate(next.startDate, locale),
      end: formatDate(addDays(next.startDate, -1), locale),
    });
  }
  if (!tone) return null;
  return (
    <p
      className={clsx(
        'mb-4 rounded-md border px-3 py-2 text-body-sm',
        tone === 'warning' ? 'border-warning/35 bg-warning/8 text-warning-fg dark:text-warning-dark' : 'border-info/30 bg-info/8 text-info-fg dark:text-info-dark',
      )}
    >
      {text}
    </p>
  );
}

/**
 * A project's links, each under its own name (operator, 2026-10-26: "add
 * project links make it unlimited links and every link has name like admin -
 * website"). Each opens in a new tab, and its URL copies with one click. They
 * are set on the project's form: whoever may edit it is offered that here, and
 * nobody else sees the card while there are none.
 */
function ProjectLinksCard({ projectId, links }: { projectId: string; links: Array<{ id: string; name: string; url: string }> }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const edits = can(P.PROJECTS_MANAGE);
  if (!links.length && !edits) return null;

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('projects.linkCopied'));
    } catch {
      // A browser that refuses the clipboard says so: the URL is on screen to copy by hand.
      toast.error(t('projects.copyRefused'));
    }
  };

  return (
    <Card>
      <CardHeader
        title={t('projects.links')}
        action={
          edits && links.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => navigate(`/projects/${projectId}/edit`)}>
              {t('common.edit')}
            </Button>
          )
        }
      />
      {links.length > 0 ? (
        <ul className="-my-2 divide-y divide-[var(--line)]">
          {links.map((link) => (
            <li key={link.id} className="flex items-center gap-2 py-2">
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex min-w-0 flex-1 items-center gap-3 rounded-md py-1"
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border hairline bg-[var(--surface-sunken)] text-muted transition-colors duration-fast group-hover:text-primary-600 dark:group-hover:text-primary-400"
                  aria-hidden
                >
                  <Link2 className="h-4 w-4" />
                </span>
                {/* The name and the URL keep their own direction and line up with the page's. */}
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-body font-medium text-[var(--text-primary)] group-hover:text-primary-600 dark:group-hover:text-primary-400">
                    <bdi>{link.name}</bdi>
                  </span>
                  <span className="block">
                    <span dir="ltr" className="break-all font-mono text-mono-sm text-muted">{link.url}</span>
                  </span>
                </span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
              </a>
              <Button
                size="sm"
                variant="ghost"
                icon={<Copy className="h-4 w-4" />}
                aria-label={t('projects.copyLink')}
                title={t('projects.copyLink')}
                onClick={() => void copy(link.url)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-body-sm text-muted">{t('projects.noLinks')}</p>
          <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => navigate(`/projects/${projectId}/edit`)}>
            {t('projects.addLinks')}
          </Button>
        </div>
      )}
    </Card>
  );
}

export function ProjectsPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  // Kept until cleared: opening a project and coming back finds the list as it was left.
  const [filters, setFilters] = useKeptFilters('projects');
  const setFilter = (key: string, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['projects', page, filters],
    queryFn: () => api.get<Paginated<any>>('/projects', { page, pageSize: 25, ...filters }),
  });

  return (
    <>
      <PageHeader
        title={t('nav.projects')}
        count={data?.total}
        // §7.4 — a user without PROJECTS_VIEW_ALL sees a narrowed list, and the
        // count above is that narrowed total, so say why.
        subtitle={can(P.PROJECTS_VIEW_ALL) ? undefined : t('projects.ownOnlyNote')}
        action={
          can(P.PROJECTS_CREATE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/projects/new')}>
              {t('common.create')}
            </Button>
          )
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <Input placeholder={t('common.search')} value={filters.q ?? ''} onChange={(e) => setFilter('q', e.target.value)} />
          <Select value={filters.status ?? ''} onChange={(e) => setFilter('status', e.target.value)}>
            <option value="">{t('common.status')} — {t('common.all')}</option>
            {PROJECT_STATUSES.map((s) => (
              <option key={s} value={s}>{projectStatusLabel(s, locale)}</option>
            ))}
          </Select>
          <Select value={filters.priority ?? ''} onChange={(e) => setFilter('priority', e.target.value)}>
            <option value="">{t('common.priority')} — {t('common.all')}</option>
            {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => (
              <option key={p} value={p}>{enumLabel(p, locale)}</option>
            ))}
          </Select>
        </div>
        {anyFilter(filters) && (
          <div className="flex flex-wrap gap-2 border-t hairline px-4 py-2.5">
            <Button size="sm" variant="ghost" onClick={() => { setFilters({}); setPage(1); }}>
              {t('common.clear')}
            </Button>
          </div>
        )}
      </Card>

      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.items.length ? (
        <Card><EmptyState icon={<Building2 className="h-8 w-8" />} /></Card>
      ) : (
        <>
          {/* One column of minmax(0, 1fr) on a phone: a long project name truncates
              instead of widening every card past the screen. */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {data.items.map((project) => (
              <button
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="surface rounded-lg p-5 text-start transition-colors duration-fast hover:border-primary/50"
              >
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{project.code}</span>
                    <h3 className="truncate text-h3 text-[var(--text-primary)]">{localized(project)}</h3>
                  </div>
                  <span className="flex shrink-0 flex-col items-end gap-1">
                    <ProjectStatusBadge project={project} />
                  </span>
                </div>

                {project.description && (
                  <p className="mb-3 line-clamp-2 text-body-sm text-muted">{project.description}</p>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={priorityTone(project.priority)}>{enumLabel(project.priority, locale)}</Badge>
                  <span className="text-caption text-muted">
                    {project._count.members} {t('departments.members').toLowerCase()}
                  </span>
                  <span className="text-caption text-muted">
                    {project._count.tasks} {t('nav.tasks').toLowerCase()}
                  </span>
                  <span className="text-caption text-muted">
                    {project._count.tickets} {t('nav.tickets').toLowerCase()}
                  </span>
                </div>

                <p className="mt-3 border-t hairline pt-2 text-caption text-faint">
                  {localized(project.owner)}
                  {project.expectedEndDate && ` · ${formatDate(project.expectedEndDate, locale)}`}
                </p>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <Card padded={false}>
              <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
            </Card>
          </div>
        </>
      )}

      
    </>
  );
}

export function ProjectDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [addingMember, setAddingMember] = useState(false);

  const { data: project, isLoading, error, refetch } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.get<any>(`/projects/${id}`),
  });

  const { data: tasks } = useQuery({
    queryKey: ['project', id, 'tasks'],
    queryFn: () => api.get<any[]>(`/projects/${id}/tasks`),
    enabled: tab === 'tasks',
  });

  const { data: tickets } = useQuery({
    queryKey: ['project', id, 'tickets'],
    queryFn: () => api.get<any[]>(`/projects/${id}/tickets`),
    enabled: tab === 'tickets',
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.del(`/projects/${id}/members/${userId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['project', id] }),
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  // §7.5 — an invisible project comes back as 404, and this is what the user sees.
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div className="mb-4">
        <Link to="/projects" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('nav.projects')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{project.code}</span>
            <span>{localized(project)}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <ProjectStatusBadge project={project} />
            <Badge tone={priorityTone(project.priority)}>{enumLabel(project.priority, locale)}</Badge>
            {/* CR-001 §8/§45 — a project is a client project or an internal one,
                and which it is should be visible without opening a tab. */}
            {project.client ? (
              <Link to={`/clients/${project.client.id}`}>
                <Badge tone="brand">
                  {locale === 'ar' && project.client.companyNameAr
                    ? project.client.companyNameAr
                    : project.client.companyName}
                </Badge>
              </Link>
            ) : (
              <Badge tone="neutral">{t('clients.internalProject')}</Badge>
            )}
            <span className="text-muted">{localized(project.owner)}</span>
          </span>
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            {can(P.PROJECTS_MANAGE) && (
              <>
                {/* The status on its own, beside Edit; the edit form keeps its Status too (2026-10-09). */}
                <ProjectStatusMenu project={project} />
                <Button onClick={() => navigate(`/projects/${id}/edit`)}>{t('common.edit')}</Button>
              </>
            )}
            {/* Team leaders add people too, and take nobody off (2026-10-11). */}
            {can(P.PROJECT_MEMBERS_ADD) && (
              <Button icon={<UserPlus className="h-4 w-4" />} onClick={() => setAddingMember(true)}>
                {t('projects.addMember')}
              </Button>
            )}
          </div>
        }
      />

      <SupportNote project={project} />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: t('tickets.details') },
          { id: 'members', label: t('departments.members'), count: project.members.length },
          { id: 'tasks', label: t('nav.tasks'), count: project._count.tasks },
          { id: 'tickets', label: t('nav.tickets'), count: project._count.tickets },
          // The Drive folders and files about it that the reader may open (2026-11-02).
          { id: 'drive', label: t('drive.tab') },
        ]}
      />

      <div className="mt-5">
        {tab === 'overview' && (
          // One column of minmax(0, 1fr) on a phone, so a long link wraps instead of widening the page.
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="min-w-0 space-y-5 lg:col-span-2">
              <Card>
                <CardHeader title={t('common.description')} />
                <p className="whitespace-pre-wrap text-body text-[var(--text-primary)]">
                  {project.description || '—'}
                </p>
              </Card>
              <ProjectLinksCard projectId={id} links={project.links ?? []} />
            </div>
            <Card className="min-w-0">
              <CardHeader title={t('tickets.details')} />
              <dl className="divide-y divide-[var(--line)]">
                <Detail label={t('common.status')}>
                  {projectStatusLabel(project.status, locale)}
                  {project.status === 'SUPPORT' && project.supportByHand && (
                    <span className="text-muted"> · {t('projects.byHand')}</span>
                  )}
                </Detail>
                {project.support?.contracts?.length > 0 && (
                  <Detail label={t('projects.support')}>
                    <span className="block">
                      {t('projects.supportUntil', { date: formatDate(project.support.until, locale) })}
                    </span>
                    <span className="block font-mono text-mono-sm text-muted" dir="ltr">
                      {project.support.contracts.map((c: { number: string }) => c.number).join(', ')}
                    </span>
                  </Detail>
                )}
                <Detail label={t('common.priority')}>{enumLabel(project.priority, locale)}</Detail>
                <Detail label="Owner">{localized(project.owner)}</Detail>
                <Detail label={t('invoices.client')}>
                  {project.client ? (
                    <Link to={`/clients/${project.client.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                      {locale === 'ar' && project.client.companyNameAr
                        ? project.client.companyNameAr
                        : project.client.companyName}
                    </Link>
                  ) : (
                    <span className="text-faint">{t('clients.internalProject')}</span>
                  )}
                </Detail>
                <Detail label={t('common.from')}>{formatDate(project.startDate, locale)}</Detail>
                <Detail label={t('common.to')}>{formatDate(project.expectedEndDate, locale)}</Detail>
                <Detail label={t('common.createdAt')}>{formatDate(project.createdAt, locale)}</Detail>
              </dl>
            </Card>

            {project.activities?.length > 0 && (
              <Card className="lg:col-span-3">
                <CardHeader title={t('tickets.timeline')} />
                <ul className="space-y-2">
                  {project.activities.map((a: any) => (
                    <li key={a.id} className="flex flex-wrap items-baseline gap-2 text-body-sm">
                      <span className="tabular text-caption text-faint">{formatDateTime(a.occurredAt, locale)}</span>
                      <span className="text-[var(--text-primary)]">{a.message ?? a.action}</span>
                      {a.actor && <span className="text-caption text-muted">· {a.actor.fullName}</span>}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        )}

        {tab === 'members' && (
          <Card padded={false}>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('common.email')}</Th>
                  <Th>{t('common.role')}</Th>
                  {/* Taking someone off is projects.manage's: the team leaders only add. */}
                  {can(P.PROJECTS_MANAGE) && <Th align="end">{t('common.actions')}</Th>}
                </tr>
              </thead>
              <tbody>
                {project.members.map((m: any) => (
                  <Tr key={m.id}>
                    <Td>{localized(m.user)}</Td>
                    <Td className="text-muted" dir="ltr">{m.user.email}</Td>
                    <Td>
                      <Badge tone={m.roleInProject === 'OWNER' ? 'brand' : 'neutral'}>
                        {m.roleInProject ?? 'MEMBER'}
                      </Badge>
                    </Td>
                    {can(P.PROJECTS_MANAGE) && (
                      <Td align="end">
                        {m.userId !== project.ownerUserId && (
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<UserMinus className="h-4 w-4" />}
                            onClick={() => removeMember.mutate(m.userId)}
                          >
                            {t('common.remove')}
                          </Button>
                        )}
                      </Td>
                    )}
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}

        {tab === 'tasks' && (
          <Card padded={false}>
            {!tasks?.length ? (
              <EmptyState />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.name')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th>{t('common.assignee')}</Th>
                    <Th align="end">{t('common.dueDate')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => (
                    <Tr key={task.id}>
                      <Td>
                        {/* The API lists the open urgent ones first (2026-10-11); this says why. */}
                        <span className="flex items-center gap-2">
                          <span className="min-w-0">{task.title}</span>
                          {isOpenUrgent(task) && <Badge tone="error">{enumLabel('URGENT', locale)}</Badge>}
                        </span>
                      </Td>
                      <Td><Badge tone={statusTone(task.status)}>{enumLabel(task.status, locale)}</Badge></Td>
                      <Td className="text-muted"><TaskHolderName task={task} empty="—" /></Td>
                      <Td align="end" className="text-muted tabular">{formatDate(task.dueAt, locale)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}

        {tab === 'tickets' && (
          <Card padded={false}>
            {!tickets?.length ? (
              <EmptyState />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('tickets.number')}</Th>
                    <Th>{t('tickets.subject')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th>{t('common.department')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <Tr key={ticket.id}>
                      <Td>
                        <Link to={`/tickets/${ticket.id}`} className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">
                          {ticket.number}
                        </Link>
                      </Td>
                      <Td>{ticket.subject}</Td>
                      <Td><Badge tone={statusTone(ticket.status)}>{enumLabel(ticket.status, locale)}</Badge></Td>
                      <Td className="text-muted">{localized(ticket.targetDepartment)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}

        {tab === 'drive' && <DriveRelated projectId={id} />}
      </div>

      <AddProjectMemberModal
        projectId={id}
        ownerUserId={project.ownerUserId}
        open={addingMember}
        onClose={() => setAddingMember(false)}
        members={project.members}
        onAdded={() => setTab('members')}
      />
    </>
  );
}

/** What `POST /projects/:id/members/from-department` answers. */
interface DepartmentAdded {
  department: { id: string; name: string; nameAr: string | null };
  added: Array<{ id: string; fullName: string; fullNameAr: string | null }>;
  alreadyOnProject: number;
}

/**
 * Adding a member is a dialog, not a page (operator's call, 2026-09-22) — the
 * deliberate exception to the "every form is a page" rule. It is two fields,
 * and it belongs beside the member list you are already looking at; routing away
 * and back to add one person costs more than the popup does.
 *
 * It adds one person, or everyone in a department at once (operator,
 * 2026-10-07). A department lists who it will bring before anything is saved.
 * Both come from the adder's own departments, any with users.view_all, as the
 * API takes them. Team leaders add too, and take nobody off (2026-10-11).
 */
function AddProjectMemberModal({
  projectId,
  ownerUserId,
  open,
  onClose,
  members,
  onAdded,
}: {
  projectId: string;
  ownerUserId: string;
  open: boolean;
  onClose: () => void;
  members: Array<{ userId: string }>;
  onAdded: () => void;
}) {
  const { t, locale, localized } = useI18n();
  const { user, can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'person' | 'department'>('person');
  const seesAll = can(P.USERS_VIEW_ALL);
  const [userId, setUserId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [roleInProject, setRoleInProject] = useState('MEMBER');

  const { data: users } = useQuery({
    queryKey: ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options'),
    enabled: open && mode === 'person',
  });
  const { data: departments } = useQuery({
    queryKey: ['departments', 'picker'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options'),
    enabled: open && mode === 'department',
  });
  // Who the department brings, counted as the API counts them: its active people today.
  const departmentPeople = useQuery({
    queryKey: ['users', 'options', { departmentId }],
    queryFn: () => api.get<UserRef[]>('/users/options', { departmentId }),
    enabled: open && mode === 'department' && !!departmentId,
  });

  const close = () => {
    setMode('person');
    setUserId('');
    setDepartmentId('');
    setRoleInProject('MEMBER');
    onClose();
  };

  // The owner is on the project whether or not they have a member row.
  const onProject = (id: string) => id === ownerUserId || members.some((m) => m.userId === id);
  const joining = (departmentPeople.data ?? []).filter((u) => !onProject(u.id));
  const alreadyIn = (departmentPeople.data?.length ?? 0) - joining.length;
  // One's own departments — every one with users.view_all — as the API takes them.
  const mine = new Set((user?.departments ?? []).map((d) => d.id));
  const offered = (departments ?? []).filter((d) => seesAll || mine.has(d.id));
  const department = offered.find((d) => d.id === departmentId);

  const add = useMutation({
    mutationFn: () =>
      mode === 'person'
        ? api.post<unknown>(`/projects/${projectId}/members`, { userId, roleInProject })
        : api.post<DepartmentAdded>(`/projects/${projectId}/members/from-department`, { departmentId, roleInProject }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      if (mode === 'department') {
        const done = result as DepartmentAdded;
        const name = localized(done.department);
        toast.success(
          done.added.length
            ? [
                t('projects.departmentAdded', { n: formatNumber(done.added.length, locale), department: name }),
                done.alreadyOnProject ? t('projects.departmentAlready', { n: formatNumber(done.alreadyOnProject, locale) }) : '',
              ]
                .filter(Boolean)
                .join(' ')
            : t('projects.departmentAddedNone', { department: name }),
        );
      }
      onAdded();
      close();
    },
    onError: (e: unknown) => toast.error(refusalText(e, t)),
  });

  // Someone already on the project is not a candidate: the endpoint upserts, so
  // picking them would silently rewrite their role instead of adding anyone (and
  // is refused to whoever only adds).
  const candidates = (users ?? []).filter((u) => !onProject(u.id));
  const ready =
    mode === 'person'
      ? !!userId
      : !!departmentId && departmentPeople.isSuccess && !departmentPeople.isFetching && joining.length > 0;

  const modes = [
    {
      id: 'person' as const,
      label: t('projects.addPerson'),
      hint: t(seesAll ? 'projects.addPersonHint' : 'projects.addPersonMineHint'),
      icon: User,
    },
    {
      id: 'department' as const,
      label: t('projects.addDepartment'),
      hint: t(seesAll ? 'projects.addDepartmentHint' : 'projects.addDepartmentMineHint'),
      icon: Users,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('projects.addMember')}
      size="sm"
      footer={
        <>
          <Button type="button" onClick={close}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            form="add-project-member"
            variant="primary"
            loading={add.isPending}
            disabled={!ready}
          >
            {t('common.add')}
          </Button>
        </>
      }
    >
      <form
        id="add-project-member"
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready && !add.isPending) add.mutate();
        }}
      >
        <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('projects.addMember')}>
          {modes.map(({ id: option, label, hint, icon: Icon }) => (
            <label
              key={option}
              className={clsx(
                'flex cursor-pointer gap-2.5 rounded-md border p-3 transition-colors duration-fast',
                mode === option ? 'border-primary bg-primary/8' : 'hairline hover:border-primary/40',
              )}
            >
              <input
                type="radio"
                name="add-member-mode"
                value={option}
                checked={mode === option}
                onChange={() => setMode(option)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-body-sm font-medium text-[var(--text-primary)]">
                  <Icon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                  {label}
                </span>
                <span className="mt-0.5 block text-caption text-muted">{hint}</span>
              </span>
            </label>
          ))}
        </div>

        {mode === 'person' ? (
          <Field label={t('common.name')} required>
            <Select value={userId} onChange={(e) => setUserId(e.target.value)} autoFocus>
              <option value="">—</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>{localized(u)}</option>
              ))}
            </Select>
          </Field>
        ) : (
          <>
            <Field
              label={t('common.department')}
              required
              error={departments && !offered.length ? t('projects.noDepartmentOfYours') : null}
            >
              <Select
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                disabled={!!departments && !offered.length}
                autoFocus
              >
                <option value="">—</option>
                {offered.map((d) => (
                  <option key={d.id} value={d.id}>{localized(d)}</option>
                ))}
              </Select>
            </Field>
            {departmentId && departmentPeople.isLoading && (
              <p className="text-caption text-faint">{t('common.loading')}</p>
            )}
            {departmentId &&
              departmentPeople.isSuccess &&
              (joining.length > 0 ? (
                <div>
                  <p className="text-body-sm font-medium text-[var(--text-secondary)]">
                    {t('projects.departmentWillAdd', { n: formatNumber(joining.length, locale) })}
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {joining.map((u) => (
                      <li
                        key={u.id}
                        className="rounded-pill bg-primary/12 px-2.5 py-1 text-body-sm text-[var(--text-primary)] ring-1 ring-inset ring-primary/30"
                      >
                        {localized(u)}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-caption text-faint">
                    {alreadyIn > 0 && `${t('projects.departmentAlready', { n: formatNumber(alreadyIn, locale) })} `}
                    {t('projects.departmentLater', { department: localized(department) })}
                  </p>
                </div>
              ) : (
                <p className="rounded-md border border-warning/35 bg-warning/8 p-3 text-body-sm text-[var(--text-primary)]">
                  {departmentPeople.data.length ? t('projects.departmentAllIn') : t('projects.departmentEmpty')}
                </p>
              ))}
          </>
        )}

        <Field label={t('common.role')} hint={mode === 'department' ? t('projects.departmentRoleHint') : undefined}>
          <Select value={roleInProject} onChange={(e) => setRoleInProject(e.target.value)}>
            <option value="MEMBER">{enumLabel('MEMBER', locale)}</option>
            <option value="LEADER">{enumLabel('LEADER', locale)}</option>
          </Select>
        </Field>

        {/* Whoever only adds — a team leader — cannot undo it (2026-10-11). */}
        {!can(P.PROJECTS_MANAGE) && <p className="text-caption text-muted">{t('projects.addOnlyNote')}</p>}
      </form>
    </Modal>
  );
}
